import React, { useState, useEffect, useRef } from 'react';
import {
    Paper,
    Typography,
    Box,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Button,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete } from '@mui/material';
import { debounce } from 'lodash';
import { API } from '../../api/api';
import { END_POINTS } from '../../api/endpoints';
import { store } from '../../redux/store';
import * as slotActions from '../../redux/actions/slotsActions'
import moment from 'moment';
import { useSelector } from 'react-redux';
import * as appointmentActions from '../../redux/actions/appointmentActions';
import { useToast } from '../../context/ToastContext';
import PatientRegistrationForm from './RegistrationForm';
import usePermissions from "../../utils/permissions";

export default function ModalComponent({ openBookAppointment, patientReffered, clearBookAppointment }) {
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [activeSlots, setDoctorSlots] = useState([]);
    const [slot, setSlot] = useState({});
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [patient, setPatient] = useState({})
    const slotReducer = useSelector((state) => state.slotReducer)
    const appointmentReducer = useSelector((state) => state.appointmentReducer)
    const inputRef = useRef(null);
    const showToast = useToast()
    const [blurBg, setBlurBg] = useState(false)
    const { hasPermission } = usePermissions();
    const [isSubmittingAppointment, setIsSubmittingAppointment] = useState(false);
    const [paymentType, setPaymentType] = useState('POSTPAID');

    useEffect(() => {
        store.dispatch(slotActions.getDoctorSlotsRequestAction(moment(date).format('YYYY-MM-DD')))
    }, [showDropdown])

    useEffect(() => {
        if (slotReducer?.slots && slotReducer.slots !== activeSlots) {
            setDoctorSlots(slotReducer.slots);
            const firstAvailable = (slotReducer?.slots || []).find(slot => slot.isAvailable === true && moment(slot.startDateTime).isAfter(moment())) || {};
            setSlot(firstAvailable)
        }
    }, [slotReducer?.slots, activeSlots]);

    useEffect(() => {
        if (openBookAppointment) {
            const firstAvailable = (slotReducer?.slots || []).find(slot => slot.isAvailable === true && moment(slot.startDateTime).isAfter(moment())) || {};
            setShowDropdown((prev) => !prev);
            setPatient(patientReffered)
            setSlot(firstAvailable)
        }
    }, [openBookAppointment])

    useEffect(() => {
        if (appointmentReducer?.appointmentCreated?.id) {
            showToast("Appointment Booked", "info");
            setIsSubmittingAppointment(false);
            setShowDropdown((prev) => !prev);
            setPatient({})
            setSlot({})
            setPaymentType('POSTPAID')
            setDate(new Date().toISOString().split('T')[0])
            store.dispatch(appointmentActions.resetAppointmentState())
        }
    }, [appointmentReducer?.appointmentCreated]);

    useEffect(() => {
        if (
          isSubmittingAppointment &&
          appointmentReducer.status === "Appointment creation failure"
        ) {
          showToast(appointmentReducer.error, "info");
          setIsSubmittingAppointment(false);
      
          store.dispatch(
            appointmentActions.resetAppointmentState()
          );
        }
      }, [
        appointmentReducer.status,
        isSubmittingAppointment,
      ]);

    const fetchPatients = debounce(async (name) => {
        if (!name) {
            setOptions([]);
            return;
        }

        setLoading(true);
        try {
            let baseUrl =
                process.env.NODE_ENV === 'development'
                    ? process.env.REACT_APP_DEVELOP
                    : process.env.REACT_APP_PROD;
            const patient = await API.get(`${baseUrl}${END_POINTS.searchPatient(name)}`)
            const data = patient.data
            // const filtered = data.filter((p) =>
            //     p.firstName.toLowerCase().includes(name.toLowerCase())
            // );
            setOptions(data);
        } catch (err) {
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    }, 300);

    useEffect(() => {
        fetchPatients(inputValue);
    }, [inputValue]);


    const handleChange = () => {
        const firstAvailable = (slotReducer?.slots || []).find(slot => slot.isAvailable === true && moment(slot.startDateTime).isAfter(moment())) || {};
        setShowDropdown((prev) => !prev);
        setPatient({})
        setSlot(firstAvailable)
        setPaymentType('POSTPAID')
        setDate(new Date().toISOString().split('T')[0])
        clearBookAppointment()
    };

    const handleDate = (e) => {
        setDate(e.target.value)
        store.dispatch(slotActions.getDoctorSlotsRequestAction(e.target.value))
    }

    const createAppointment = () => {
        setIsSubmittingAppointment(true);
        clearBookAppointment()
        store.dispatch(appointmentActions.createAppointmentRequestAction(
            patient?.id,
            slot?.id,
            slot?.hospitalId,
            paymentType,
        ))
    }

    const handleClose = () => {
        setBlurBg((prev) => !prev)
    }

    const addPatient = (data) => {
        setPatient(data)
    }

    return (
        <Box sx={{ position: 'relative', }}>
            {/* Blurred view */}
            {showDropdown && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backdropFilter: 'blur(4px)',
                        zIndex: 998,
                    }}
                />
            )}
            <Typography sx={{ height: '36px', alignContent: 'center', border: '1px solid #919191', color: 'rgba(84, 84, 84, 1)', borderRadius: 2, textAlign: 'left', px: 1 }} fontSize={"12px"} onClick={() => handleChange()}>
                New Appointment
            </Typography>

            {/* Dropdown Results */}
            {showDropdown && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        mt: 1,
                        zIndex: 999,
                        overflowY: 'auto',
                        borderRadius: 2,
                        p: 1,
                        maxHeight: { xs: '80vh', md: 'none' },
                        boxSizing: 'border-box',
                        width: { xs: '100%', sm: '100%', xl: 'auto', md: '32vw', lg: '28vw' },
                        maxWidth: { xs: '100%', sm: '100%', md: '90vw' },
                    }}
                >
                    <Box sx={{ backgroundColor: 'white', borderRadius: 2 }}>
                        <IconButton
                            onClick={() => { handleChange() }}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1000,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" align="center" gutterBottom>
                            New Appointment
                        </Typography>
                        <Autocomplete
                            freeSolo
                            disableClearable
                            value={patient?.firstName || patient?.lastName
                                ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
                                : ''}
                            loading={loading}
                            options={options}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.firstName + " " + option.lastName}
                            onInputChange={(event, newInputValue) => {
                                setInputValue(newInputValue);
                                if (newInputValue.length == 0) {
                                    setPatient({})
                                }

                            }}
                            onChange={(event, newValue) => {
                                if (typeof newValue === 'object' && newValue !== null) {
                                    setPatient(newValue)
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Name/Phn no."
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            sx={{
                                mt: 1,
                                zIndex: 1200,
                                position: 'relative'
                            }}
                        />

                        <TextField
                            label="Patient ID"
                            variant="outlined"
                            fullWidth
                            value={patient?.patientId || ''}
                            disabled
                            sx={{ mt: 1 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', }} onClick={() => inputRef.current && inputRef.current.showPicker()}>
                            <TextField
                                inputRef={inputRef}
                                label="Date"
                                type="date"
                                variant="outlined"
                                value={date}
                                onChange={(e) => {
                                    handleDate(e)
                                    setSlot({})
                                }
                                }
                                margin="normal"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: new Date().toISOString().split("T")[0],
                                    max: moment().add(13, 'days').format('YYYY-MM-DD')

                                }}
                                sx={{ mt: 1, width: '45%', }}
                            />
                            <FormControl sx={{ mt: 1, width: '45%' }}>
                                <InputLabel>Slot</InputLabel>
                                <Select
                                    value={slot}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setSlot(e.target.value)}
                                    label="Slot"
                                >
                                    {activeSlots.map((slot, index) => {
                                        const isToday = moment(date).isSame(moment(), 'day');
                                        const slotTime = moment.utc(slot.startDateTime).local();
                                        const isPast = isToday && slotTime.isBefore(moment());
                                        const hospitalName = slot.hospitalName || slot.hospital?.name;
                                        return (
                                            <MenuItem key={slot.id || index} value={slot} disabled={isPast || !slot.isAvailable}>
                                                {moment(slot.startDateTime).format('hh:mm A')}{hospitalName ? ` (${hospitalName})` : ''}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                       

                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel>Payment timing</InputLabel>
                            <Select
                                value={paymentType}
                                onChange={(e) => setPaymentType(e.target.value)}
                                label="Payment timing"
                            >
                                <MenuItem value="POSTPAID">Pay later (online or at clinic)</MenuItem>
                                <MenuItem value="PREPAID">Pay before appointment</MenuItem>
                            </Select>
                        </FormControl>

                        <Box display="flex" justifyContent='space-between' sx={{ mt: 2 }}>
                            {hasPermission('PATIENT_CREATE') && <Button
                                variant="contained"
                                onClick={() => setBlurBg(!blurBg)}
                                color="primary"
                                sx={{ height: '40px', width: '150px', fontSize: '13px', backgroundColor: 'rgba(221, 238, 253, 1)', color: 'black', borderRadius: '10px' }}
                            >
                                Add new
                            </Button>}
                            <Button
                                variant="contained"
                                onClick={() => createAppointment()}
                                color="primary"
                                disabled={!patient.patientId || !slot.id || !date}
                                sx={{ height: '40px', width: '150px', fontSize: '13px', backgroundColor: 'rgba(221, 238, 253, 1)', color: 'black', borderRadius: '10px' }}
                            >
                                Confirm
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            {blurBg && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backdropFilter: 'blur(10px)',
                        zIndex: 998,
                    }}
                />
            )}

            {/* create new patient */}
            {blurBg && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'fixed',
                        top: { xs: '5%', md: '10%' },
                        left: { xs: '2.5%', md: '50%' },
                        transform: { xs: 'none', md: 'translateX(-50%)' },
                        zIndex: 1000,
                        borderRadius: 2,
                        p: { xs: 2, md: 3 },
                        backgroundColor: '#F2F2F2',
                        width: { xs: '95%', sm: '95%', md: '808px' },
                        maxWidth: { xs: '95vw', md: '95vw' },
                        maxHeight: { xs: '90vh', md: '85vh' },
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                    }}
                >
                    <IconButton
                        onClick={() => { handleClose() }}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 1,
                            zIndex: 1000,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box sx={{ flexDirection: { xs: 'column', sm: 'row' }, display: 'flex', justifyContent: 'space-between', width: { xs: '100%', sm: '90%' }, gap: { xs: 1, sm: 0 }, pr: { xs: 4, sm: 0 } }}>
                        <Typography textAlign={'left'} sx={{
                            // width: '199px',
                            height: '19px',
                            position: 'relative',
                            fontSize: '17px',
                            fontWeight: '700',
                            color: '#343333',
                            textAlign: 'left',
                            // display: 'inline-block'
                        }}>
                            New Patient Registration
                        </Typography>
                        <Typography sx={{
                            position: 'relative',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#343333',
                            textAlign: 'left',
                        }}>
                            * Required Fields
                        </Typography>
                    </Box>
                    <PatientRegistrationForm handleClose={handleClose} addPatient={addPatient} />
                </Paper>
            )}
        </Box>
    );
}

